import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { clientIntegrationService, ClientIntegration } from '../services/clientIntegrationService';
import Footer from 'components/PageFooter';
import { EBAY_OAUTH_AUTHORIZE_URL } from '../config/constants';

interface PlatformConfig {
  [key: string]: any;
}

const ApiManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialIntegrations, setInitialIntegrations] = useState<ClientIntegration[]>([]);
  const [showWooWpAppPassword, setShowWooWpAppPassword] = useState(true);

  const [woocommerce, setWoocommerce] = useState({
    active: false,
    store_url: '',
    consumer_key: '',
    consumer_secret: '',
    wpUser: '',
    wpAppPassword: ''
  });

  const [ebay, setEbay] = useState({
    active: false,
    app_id: '',
    cert_id: '',
    dev_id: '',
    auth_token: '',
    environment: 'production'
  });

  const [delcampe, setDelcampe] = useState({
    active: false,
    api_key: '',
    api_secret: ''
  });

  const [etsy, setEtsy] = useState({
    active: false,
    api_key: '',
    shared_secret: '',
    oauth_token: '',
    oauth_token_secret: '',
    shop_id: ''
  });

  const [hipstamp, setHipstamp] = useState({
    active: false,
    api_key: '',
    store_id: ''
  });

  const [hippostcard, setHippostcard] = useState({
    active: false,
    api_key: '',
    store_id: ''
  });

  useEffect(() => {
    if (!user?.clientId) {
      setLoading(false);
      return;
    }
    loadIntegrations();
  }, [user?.clientId]);

  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const isEqual = (obj1: Record<string, any>, obj2: Record<string, any>) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every(key => obj1[key] === obj2[key]);
  }

  const isEmpty = (credentials: Record<string, any>) => {
    return Object.values(credentials).every(
      value => value === null || value === undefined || value === ''
    );
  };

  const credentialsMeaningfullyEmpty = (platformName: string, creds: Record<string, any>) => {
    const copy = { ...creds };
    if (platformName === 'ebay') {
      delete copy.environment;
    }
    return Object.values(copy).every((v) => v == null || v === '');
  };

  const loadIntegrations = async (options?: { silent?: boolean }) => {
    if (!user?.clientId) {
      if (!options?.silent) setLoading(false);
      return;
    }

    const showFullPageLoading = !options?.silent;
    try {
      if (showFullPageLoading) setLoading(true);
      const integrations = await clientIntegrationService.getClientIntegrations(user.clientId);
      setInitialIntegrations(integrations);

      integrations.forEach((integration: ClientIntegration) => {
        const credentials = integration.credentials || {};
          
        switch (integration.platform) {
          case 'woocommerce':
            const wpAppPasswordValue = credentials.wpAppPassword || '';
            setWoocommerce({
              active: integration.is_active,
              store_url: credentials.store_url || '',
              consumer_key: credentials.consumer_key || '',
              consumer_secret: credentials.consumer_secret || '',
              wpUser: credentials.wpUser || '',
              wpAppPassword: wpAppPasswordValue
            });
            setShowWooWpAppPassword(!wpAppPasswordValue);
            break;
          case 'ebay':
            setEbay({
              active: integration.is_active,
              app_id: credentials.app_id || '',
              cert_id: credentials.cert_id || '',
              dev_id: credentials.dev_id || '',
              auth_token: credentials.auth_token || '',
              environment: credentials.environment || 'production'
            });
            break;
          case 'delcampe':
            setDelcampe({
              active: integration.is_active,
              api_key: credentials.api_key || '',
              api_secret: credentials.api_secret || ''
            });
            break;
          case 'etsy':
            setEtsy({
              active: integration.is_active,
              api_key: credentials.api_key || '',
              shared_secret: credentials.shared_secret || '',
              oauth_token: credentials.oauth_token || '',
              oauth_token_secret: credentials.oauth_token_secret || '',
              shop_id: credentials.shop_id || ''
            });
            break;
          case 'hipstamp':
            setHipstamp({
              active: integration.is_active,
              api_key: credentials.api_key || '',
              store_id: credentials.store_id || ''
            });
            break;
          case 'hippostcard':
            setHippostcard({
              active: integration.is_active,
              api_key: credentials.api_key || '',
              store_id: credentials.store_id || ''
            });
            break;
        }
      });
    } catch (err) {
      console.error('Error loading integrations:', err);
      const error = err;
      if (error && error.response && error.response.status !== 404) {
        setError('Failed to load integrations');
      }
    } finally {
      if (showFullPageLoading) setLoading(false);
    }
  };

  type PlatformPayload = {
    name: string;
    data: { active: boolean };
    credentials: Record<string, any>;
  };

  const buildPlatformPayloads = (): PlatformPayload[] => [
    {
      name: 'woocommerce',
      data: woocommerce,
      credentials: {
        store_url: woocommerce.store_url,
        consumer_key: woocommerce.consumer_key,
        consumer_secret: woocommerce.consumer_secret,
        wpUser: woocommerce.wpUser,
        wpAppPassword: woocommerce.wpAppPassword,
      },
    },
    {
      name: 'ebay',
      data: ebay,
      credentials: {
        app_id: ebay.app_id,
        cert_id: ebay.cert_id,
        dev_id: ebay.dev_id,
        auth_token: ebay.auth_token,
        environment: ebay.environment,
      },
    },
    {
      name: 'delcampe',
      data: delcampe,
      credentials: { api_key: delcampe.api_key, api_secret: delcampe.api_secret },
    },
    {
      name: 'etsy',
      data: etsy,
      credentials: {
        api_key: etsy.api_key,
        shared_secret: etsy.shared_secret,
        oauth_token: etsy.oauth_token,
        oauth_token_secret: etsy.oauth_token_secret,
        shop_id: etsy.shop_id,
      },
    },
    {
      name: 'hipstamp',
      data: hipstamp,
      credentials: { api_key: hipstamp.api_key, store_id: hipstamp.store_id },
    },
    {
      name: 'hippostcard',
      data: hippostcard,
      credentials: { api_key: hippostcard.api_key, store_id: hippostcard.store_id },
    },
  ];

  const persistPlatform = async (platform: PlatformPayload) => {
    if (!user?.clientId) return;

    const existing = initialIntegrations.find((int) => int.platform === platform.name);
    const prevCreds = existing?.credentials || {};

    const unchanged =
      !!existing &&
      isEqual(platform.credentials, prevCreds) &&
      existing.is_active === platform.data.active;

    if (unchanged) return;

    const noRecord = !existing;
    const creds = platform.credentials;
    if (
      noRecord &&
      credentialsMeaningfullyEmpty(platform.name, creds) &&
      !platform.data.active
    ) {
      return;
    }

    if (noRecord) {
      await clientIntegrationService.createIntegration(
        user.clientId,
        platform.name,
        platform.credentials,
        platform.data.active
      );
    } else {
      await clientIntegrationService.updateIntegration(
        user.clientId,
        platform.name,
        platform.credentials,
        platform.data.active
      );
    }
  };

  const handleSave = async () => {
    if (!user?.clientId) return;

    try {
      setSaving(true);
      setError(null);

      for (const platform of buildPlatformPayloads()) {
        await persistPlatform(platform);
      }

      await loadIntegrations({ silent: true });
      setSaveSuccess(true);
    } catch (err) {
      console.error('Error saving integrations:', err);
      const error = err;
      setError((error && error.response && error.response.data && error.response.data.message) || 'Failed to save integrations');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlatform = async (platformName: string) => {
    if (!user?.clientId) return;

    const platform = buildPlatformPayloads().find((p) => p.name === platformName);
    if (!platform) return;

    try {
      setSaving(true);
      setError(null);
      await persistPlatform(platform);
      await loadIntegrations({ silent: true });
      setSaveSuccess(true);
    } catch (err) {
      console.error('Error saving integration:', err);
      const error = err;
      setError((error && error.response && error.response.data && error.response.data.message) || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <PageHeader />
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <PageHeader />
        
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            API Management
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Configure your e-commerce platform integrations
          </p>
        </div>

        {saveSuccess && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg">
            <p className="text-sm text-green-800 dark:text-green-200">
              ✓ Integrations saved successfully!
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">
              {error}
            </p>
          </div>
        )}

        <div className="space-y-6">
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">WooCommerce</h3>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleSavePlatform('woocommerce')}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 text-sm text-gray-700 dark:text-gray-300">Active</span>
                  <input
                    type="checkbox"
                    checked={woocommerce.active}
                    onChange={(e) => setWoocommerce({ ...woocommerce, active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                  />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Store URL
                </label>
                <input
                  type="url"
                  value={woocommerce.store_url}
                  onChange={(e) => setWoocommerce({ ...woocommerce, store_url: e.target.value })}
                  placeholder="https://your-store.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Consumer Key
                </label>
                <input
                  type="text"
                  value={woocommerce.consumer_key}
                  onChange={(e) => setWoocommerce({ ...woocommerce, consumer_key: e.target.value })}
                  placeholder="ck_..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Consumer Secret
                </label>
                <input
                  type="password"
                  value={woocommerce.consumer_secret}
                  onChange={(e) => setWoocommerce({ ...woocommerce, consumer_secret: e.target.value })}
                  placeholder="cs_..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WP User
                </label>
                <input
                  type="text"
                  value={woocommerce.wpUser}
                  onChange={(e) => setWoocommerce({ ...woocommerce, wpUser: e.target.value })}
                  placeholder="wp_admin_ili_api_user"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WP App Password
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type={showWooWpAppPassword ? 'text' : 'password'}
                    value={woocommerce.wpAppPassword}
                    onChange={(e) => setWoocommerce({ ...woocommerce, wpAppPassword: e.target.value })}
                    placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowWooWpAppPassword((prev) => !prev)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    {showWooWpAppPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">eBay</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
                {EBAY_OAUTH_AUTHORIZE_URL ? (
                  <a
                    href={EBAY_OAUTH_AUTHORIZE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Connect eBay
                  </a>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Add <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">REACT_APP_EBAY_OAUTH_AUTHORIZE_URL</code> to{' '}
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">.env</code> to show the Connect eBay button.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save All Integrations
              </>
            )}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApiManagement;
